import { sortItemsByPredefinedOrder } from "@components/inputs/select/SelectAPI";
import { SwitchType } from "@components/inputs/switch/Switch";
import { enhanceFieldVisibility, ifAny, TGetValueFn, } from "@components/smart/FieldInfo";
import {
    EntitySetName,
    EntityTypeName,
    PrMaternityLeaveEntitlementEntity,
    PrMaternityLeaveEventEntity,
    PrPaternityLeaveEventEntity,
    PrSocialEventEntity
} from "@odata/GeneratedEntityTypes";
import { PrPaternityLeaveEntitlementCode, PrSocialEventTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import PaternityLeaveFormView from "@pages/payroll/socialEvents/eventTypes/PaternityLeaveFormView";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import { getOtherPropertyGetter } from "@pages/payroll/socialEvents/SocialEvents.utils";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, GroupedField } from "../../../../enums";
import { addFormDef, getFormGroup } from "../../../../views/formView/Form.utils";
import {
    addChildDefinitions,
    addRequiredActionSummaryDef,
    addSubmissionTableDefinitions,
    getDefinitions as commonDef,
    getSocialEventsFormDef
} from "../SocialEventsDef";


export function isOfPaternityType(...types: PrPaternityLeaveEntitlementCode[]): TGetValueFn<boolean> {
    return ({ storage }) => {
        const current = storage.getValueByPath(PrPaternityLeaveEventEntity.PaternityLeaveEntitlement, { useDirectValue: false });
        return types.includes(current);
    };
}

export const paternityLeaveEntitlementTransformation = sortItemsByPredefinedOrder([
    PrPaternityLeaveEntitlementCode.Father,
    PrPaternityLeaveEntitlementCode.Adoption,
    PrPaternityLeaveEntitlementCode.Deceased
]);

const isChildHospitalized: TGetValueFn<boolean> = ({ storage }) =>
        storage.getValueByPath(PrPaternityLeaveEventEntity.IsChildHospitalized);

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const d = cloneDeep(getSocialEventsFormDef(PaternityLeaveFormView, PrSocialEventTypeCode.PaternityLeave, context));
    const generalGroup = getFormGroup(d, SocialEventGroup.General);

    addRequiredActionSummaryDef(d);

    // add Reason and DateEventStart to general group
    addFormDef(d, PrPaternityLeaveEventEntity.PaternityLeaveEntitlement, {
        type: FieldType.ComboBox,
        width: BasicInputSizes.L,
        label: i18next.t("SocialEvents:Form.PaternityLeaveEntitlement"),
        fieldSettings: {
            displayName: PrMaternityLeaveEntitlementEntity.Name,
            transformFetchedItems: paternityLeaveEntitlementTransformation
        },
        affectedFields: [
            { id: PrPaternityLeaveEventEntity.IsChildHospitalized },
            { id: PrPaternityLeaveEventEntity.NumberTheirs },
            { id: PrPaternityLeaveEventEntity.CertificateAttachments },
        ]
    });
    addFormDef(d, PrMaternityLeaveEventEntity.DateEventStart, {
        type: FieldType.Date,
        groupedField: GroupedField.MultiStart,
        label: i18next.t("SocialEvents:Form.DateEventStart"),
        affectedFields: [
            { id: PrMaternityLeaveEventEntity.DateEventEnd, revalidate: true },
        ]
    });
    addFormDef(d, PrMaternityLeaveEventEntity.DateEventEnd, {
        type: FieldType.Date,
        isRequired: true,
        groupedField: GroupedField.MultiEnd,
        label: i18next.t("SocialEvents:Form.DateEventEnd"),
        fieldSettings: {
            minDate: getOtherPropertyGetter(PrSocialEventEntity.DateEventStart)
        },
    });
    addFormDef(d, PrPaternityLeaveEventEntity.IsChildHospitalized, {
        type: FieldType.Switch,
        label: i18next.t("SocialEvents:Declarations.IsChildHospitalized"),
        fieldSettings: {
            type: SwitchType.YesNo,
        },
        isVisible: isOfPaternityType(PrPaternityLeaveEntitlementCode.Father),
        affectedFields: [
            { id: PrPaternityLeaveEventEntity.NumberTheirs },
            { id: PrPaternityLeaveEventEntity.CertificateAttachments },
        ]
    });

    generalGroup.rows.splice(1, 0, [
        { id: PrPaternityLeaveEventEntity.PaternityLeaveEntitlement },
        { id: PrPaternityLeaveEventEntity.DateEventStart },
        { id: PrPaternityLeaveEventEntity.DateEventEnd },
    ], [
        { id: PrPaternityLeaveEventEntity.IsChildHospitalized }
    ]);

    // enhance NumberTheirs visibility: only for GivingBirth entitlement
    enhanceFieldVisibility(d, PrPaternityLeaveEventEntity.NumberTheirs, ifAny(isOfPaternityType(PrPaternityLeaveEntitlementCode.Adoption, PrPaternityLeaveEntitlementCode.Deceased), isChildHospitalized));
    enhanceFieldVisibility(d, PrPaternityLeaveEventEntity.CertificateAttachments, ifAny(isOfPaternityType(PrPaternityLeaveEntitlementCode.Adoption, PrPaternityLeaveEntitlementCode.Deceased), isChildHospitalized));

    // never required NumberTheirs for Paternity Leave
    delete d.form.fieldDefinition[PrPaternityLeaveEventEntity.NumberTheirs].isRequired;

    // add Child group to definition, including fields
    addChildDefinitions(d, PrPaternityLeaveEventEntity, false, true);

    // conditional group title
    const childGroup = getFormGroup(d, SocialEventGroup.Child);
    childGroup.title = i18next.t("SocialEvents:Form.ChildSimple");

    // Table tabs on the end of the form
    addSubmissionTableDefinitions(d);

    return {
        ...d,
        entitySet: EntitySetName.PrPaternityLeaveEvents
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    getEnumNameSpaceName(EntityTypeName.PrPaternityLeaveEntitlement)
];
setDefByEntityType(EntityTypeName.PrPaternityLeaveEvent, getDefinitions);